import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';

// The bundled catalogs are already statically imported in src/app.js,
// so importing them here doesn't grow the client bundle.
import enMessages from '../../translations/en.json';
import fiMessages from '../../translations/fi.json';

import css from './StableWidthMessage.module.css';

const BUNDLED_CATALOGS = [enMessages, fiMessages];

/**
 * Renders a translated message while reserving horizontal space for the longest
 * bundled translation of the same message id. The element keeps the same width in
 * every language, so switching languages (e.g. from the LanguageSwitcher in the
 * Topbar) doesn't push sibling elements around.
 *
 * The space is reserved with invisible "ghost" copies of the raw bundled strings,
 * so this is meant for plain-text labels only (no ICU arguments or rich-text tags).
 * If Console-hosted texts override a label, the visible label still defines the
 * minimum width, but ghost widths come from the bundled translations.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id translation key
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {ReactNode?} props.children extra content anchored to the visible label
 * (e.g. notification dot) without affecting the reserved width.
 * @returns {JSX.Element} span reserving the width of the longest bundled translation
 */
const StableWidthMessage = props => {
  const { id, className, children } = props;
  return (
    <span className={classNames(css.root, className)}>
      <span className={css.visibleLabel}>
        <FormattedMessage id={id} />
        {children}
      </span>
      {BUNDLED_CATALOGS.map((catalog, i) =>
        catalog[id] ? (
          <span key={`ghost_${i}`} className={css.ghostLabel} aria-hidden="true">
            {catalog[id]}
          </span>
        ) : null
      )}
    </span>
  );
};

export default StableWidthMessage;
